/**
 * A tiny pub/sub bridging the imperative fetch layer and React auth state.
 * `apiClient` lives outside React, so when it exhausts its refresh attempts on
 * a 401 it can't call `logout()` directly. It emits `signedOut` here instead,
 * and `AuthProvider` subscribes to drop to the signed-out state immediately
 * (rather than waiting for the next reload/navigation to re-check the session).
 *
 * Access-token rotation is also broadcast so long-lived sockets (collab WS)
 * can reauth without waiting for a 401 on the next HTTP call.
 */
type Listener = () => void
type TokenListener = (accessToken: string) => void

const signedOutListeners = new Set<Listener>()
const billingLockedListeners = new Set<Listener>()
const tokenListeners = new Set<TokenListener>()

export const sessionEvents = {
  /** Subscribe to forced sign-outs; returns an unsubscribe function. */
  onSignedOut(listener: Listener): () => void {
    signedOutListeners.add(listener)
    return () => signedOutListeners.delete(listener)
  },

  /** Notify subscribers that the session has ended and cannot be recovered. */
  emitSignedOut(): void {
    for (const listener of signedOutListeners) listener()
  },

  /**
   * Subscribe to the API refusing a request because the organization's billing
   * has locked it. Any request can be the one that finds out — a grace period
   * runs out between two clicks — so the fetch layer reports it and the app
   * swaps to its lock screen, rather than every page handling a 403 of its own.
   */
  onBillingLocked(listener: Listener): () => void {
    billingLockedListeners.add(listener)
    return () => billingLockedListeners.delete(listener)
  },

  emitBillingLocked(): void {
    for (const listener of billingLockedListeners) listener()
  },

  /** Subscribe to successful access-token rotations (after refresh). */
  onAccessTokenRotated(listener: TokenListener): () => void {
    tokenListeners.add(listener)
    return () => tokenListeners.delete(listener)
  },

  /** Notify collab / other long-lived channels that a new access token is stored. */
  emitAccessTokenRotated(accessToken: string): void {
    for (const listener of tokenListeners) listener(accessToken)
  },
}
